import type { FC, PropsWithChildren, ReactNode } from 'react';
import { createContext, useEffect, useState } from 'react';

import { css, Global } from '@emotion/react';
import type { BoxProps } from '@opendoor/bricks-next';
import { Box, Col, Loader, Row, useMedia } from '@opendoor/bricks-next';
import type { MarginProps, PaddingProps } from 'platform/bricks-next';
import { DeviceSizeContextualData } from 'platform/components/DeviceSizeContextualData';
import { Footer } from 'platform/components/layout/Footer';
import {
  DesktopPrimaryNavBar,
  MobilePrimaryNavBar,
} from 'platform/components/layout/PrimaryNavBar';
import { useBrowserCompatibilityData } from 'shared/helpers/globals';

/** @internal */
export interface LayoutProps extends PropsWithChildren {
  /** Content which is at the top of the layout. */
  header?: ReactNode;
  /** By default, Layout will render a nav bar. Set to true to override this behavior. */
  hideNav?: boolean;
  /**
   * Hides primary hub tabs (Sell, Contact, and More on mobile). Logo and account actions on
   * desktop are unchanged. Ignored when `hideNav` is true.
   */
  hideTabs?: boolean;
  /** By default, Layout will render a footer under the main content. Set to true to override this behavior. */
  hideFooter?: boolean;
  /** Content which is sticky at the bottom of the Layout. */
  stickyBottomContent?: ReactNode;
  /** If true, covers main content with a loading UI. */
  isLoading?: boolean;
  closingOrClosedOffer?: boolean;
}

/**
 * Defines a Full Page layout.
 */
export function FullPageLayout({ children }: { children: ReactNode }) {
  return (
    <Box data-testid="layout-container" tag="main" display="block">
      {children}
    </Box>
  );
}

/**
 * Defines the overall Hub layout in all form factors.
 *
 * Mobile:
 * ---------------------
 * |    Header         |
 * |-------------------|
 * |    Main           |
 * |    Content        |
 * |                   |
 * |    Footer         |
 * |-------------------|
 * |StickyBottomContent|
 * |-------------------|
 * |    Global Nav     |
 * ---------------------
 *
 * Desktop:
 * -------------------------
 * | G |    Header         |
 * | l |-------------------|
 * | o |    Main           |
 * | b |    Content        |
 * | a |                   |
 * | l |                   |
 * |   |                   |
 * | N |    Footer         |
 * | a |-------------------|
 * | v |StickyBottomContent|
 * -------------------------
 */
export const Layout: FC<LayoutProps & MarginProps & PaddingProps> = ({
  hideNav,
  hideTabs,
  hideFooter,
  header,
  children,
  stickyBottomContent,
  isLoading,
  closingOrClosedOffer,
  ...marginProps
}) => {
  const { smallerThanMD } = useMedia();
  const { isOpendoorAppEvoWebView, isOpendoorAppWebView } = useBrowserCompatibilityData();

  return (
    <>
      <DeviceSizeContextualData>
        <Row alignItems="stretch" width="100%" minHeight="100dvh">
          {/* Desktop left nav */}
          {!smallerThanMD && !hideNav && <DesktopPrimaryNavBar hideTabs={hideTabs} />}

          <Col flex={1}>
            {/* Header */}
            <Col>{header}</Col>

            {/* Main content */}
            <MainContentContainer
              hideFooter={smallerThanMD ? hideFooter || isOpendoorAppEvoWebView : hideFooter}
              isLoading={isLoading}
              closingOrClosedOffer={closingOrClosedOffer}
              {...marginProps}
            >
              {children}
            </MainContentContainer>

            {/* Sticky bottom — position: sticky for sticky bottom content and mobile nav */}
            <Col
              alignItems="stretch"
              height="auto"
              backgroundColor="$transparent"
              bottom={0}
              $platform-web={{ position: 'sticky' }}
            >
              {stickyBottomContent}
              {smallerThanMD && !hideNav && !isOpendoorAppWebView && !isOpendoorAppEvoWebView && (
                <MobilePrimaryNavBar hideTabs={hideTabs} />
              )}
            </Col>
          </Col>
        </Row>
      </DeviceSizeContextualData>

      {/* 1x1 ad tracking pixels added as first-level children of the body
          cause layout issues on pages which try to consume exactly
          100% of the vertical space. display: none hides them from the
          document flow while still triggering the requests. */}
      <Global
        styles={css`
          body > img {
            display: none;
          }
          ${isOpendoorAppEvoWebView
            ? `* {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
        -webkit-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        user-drag: none;
      }`
            : ''}
        `}
      />
    </>
  );
};

/**
 * Renders the container of the main content, for all layouts.
 *
 * The parent layout uses flex to position all items naturally.
 *
 * isLoading = true
 * ---------------------
 * |                   |
 * |    Loading        |
 * |                   |
 * |-------------------|
 * |    Footer         |
 * ---------------------
 *
 * isLoading = false | undefined
 * ---------------------
 * |    Main           |
 * |    Content        |
 * |-------------------|
 * |    Footer         |
 * ---------------------
 */
const MainContentContainer = ({
  hideFooter,
  isLoading,
  children,
  closingOrClosedOffer,
  ...marginProps
}: {
  hideFooter: boolean | undefined;
  isLoading: boolean | undefined;
  closingOrClosedOffer?: boolean;
} & MarginProps &
  PropsWithChildren) => {
  const loaderVisibilityState = useLoaderVisibilityState(isLoading);

  return (
    <Col
      data-testid="layout-container"
      backgroundColor="$backgroundPrimary"
      flex={1}
      $platform-web={{ overflowX: 'hidden' }}
      justifyContent="space-between"
      {...marginProps}
    >
      {/* Loader */}
      {loaderVisibilityState !== 'invisible' && (
        <Row
          alignItems="center"
          justifyContent="center"
          data-testid="loader-container"
          opacity={loaderVisibilityState === 'visible' ? 1 : 0}
          $platform-web={{
            transition:
              loaderVisibilityState === 'transitioning'
                ? `opacity ${LOADER_FADE_OUT_MS}ms linear`
                : 'none',
          }}
        >
          <Loader data-testid="section-loader" size="small" backgroundColor="$backgroundPrimary" />
        </Row>
      )}

      {/* Main content */}
      <LoaderVisibilityContext.Provider value={loaderVisibilityState}>
        <Box
          tag="main"
          // Hide the main content instead of not rendering at all.
          // This is necessary so that child components can do their loading logic.
          display={loaderVisibilityState !== 'invisible' ? 'none' : undefined}
        >
          {children}
        </Box>
      </LoaderVisibilityContext.Provider>

      {/* Footer */}
      {!hideFooter && (
        <Col
          tag="footer"
          alignItems="stretch"
          display={loaderVisibilityState !== 'invisible' ? 'none' : undefined}
        >
          <Footer closingOrClosedOffer={closingOrClosedOffer} />
        </Col>
      )}
    </Col>
  );
};

/**
 * Renders the content of the main content, for all layouts.
 */
export const LayoutContent: FC<BoxProps> = ({ children, p, ...props }) => (
  <Col
    data-testid="layout-content"
    mx="auto"
    maxWidth={992}
    p={p ?? '$8x'}
    $largerThanXS={{ p: p ?? '$12x' }}
    $largerThanSM={{ alignItems: 'center' }}
    $largerThanMD={{ p: p ?? '$16x', alignItems: 'center' }}
    $platform-web={{ transition: 'all 0.3s ease' }}
    {...props}
  >
    {children}
  </Col>
);

// Duration of the fade-out animation
const LOADER_FADE_OUT_MS = 300;

type VisibilityState = 'visible' | 'transitioning' | 'invisible';
const LoaderVisibilityContext = createContext<VisibilityState>('invisible');
const useLoaderVisibilityState = (isLoading: boolean | undefined): VisibilityState => {
  const [state, setState] = useState<VisibilityState>(isLoading ? 'visible' : 'invisible');

  useEffect(() => {
    if (isLoading) {
      setState('visible');
    } else {
      setState((previousState) => {
        return previousState === 'visible' ? 'transitioning' : previousState;
      });
    }
  }, [isLoading]);

  useEffect(() => {
    if (state === 'transitioning') {
      const visibilityTimeout = setTimeout(() => setState('invisible'), LOADER_FADE_OUT_MS);
      return () => clearTimeout(visibilityTimeout);
    }
  }, [state]);

  return state;
};
